import { Table, Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUser,
  faDiceThree,
  faBoltLightning,
  faGear,
  faWind,
} from '@fortawesome/free-solid-svg-icons'

const roleTypes = {
  100: 'Keeper',
  101: 'Right back',
  102: 'Right central defender',
  103: 'Middle central defender',
  104: 'Left central defender',
  105: 'Left back',
  106: 'Right winger',
  107: 'Right inner midfield',
  108: 'Middle inner midfield',
  109: 'Left inner midfield',
  110: 'Left winger',
  111: 'Right forward',
  112: 'Middle forward',
  113: 'Left forward',
}

const specialtyTypes = {
  0: 'No specialty',
  1: 'Technical',
  2: 'Quick',
  3: 'Powerful',
  4: 'Unpredictable',
  5: 'Head specialist',
  6: 'Resilient',
  8: 'Support',
}

const specialtyIcons = {
  1: faGear, // Technical
  2: faWind, // Quick
  3: faBoltLightning, // Powerful
  4: faDiceThree, // Unpredictable
  5: faUser, // Head specialist
}

const specLayout = [
  [null, null, 100, null, null], // Ylin rivi: maalivahti keskellä
  [101, 102, 103, 104, 105], // Puolustuslinja
  [106, 107, 108, 109, 110], // Keskikenttä/laidat
  [null, 111, 112, 113, null], // Hyökkäys keskellä
]

const defaultFieldRoleIds = [101, 103, 105, 106, 107, 108, 109, 110, 111, 113]

// allowedSpecialties määrittää sallitut erikoisuudet pelipaikalle
const allowedSpecialties = roleId => {
  if (roleId === 100) return [4] // Vain Unpredictable (faDiceThree) maalivahdille
  if ([107, 108, 109].includes(roleId)) return [1, 2, 3, 4, 5] // Lisää Powerful (3) inner-paikoille
  if ([111, 112, 113].includes(roleId)) return [1, 2, 3, 4, 5] // Lisää Powerful (3) hyökkääjille
  if ([101, 102, 103, 104, 105, 106, 110].includes(roleId)) return [1, 2, 4, 5] // Muut kenttäpelaajat
  return []
}

const behaviourOptions = roleId => {
  if (roleId === 100) return [0]
  if ([101, 105, 106, 110].includes(roleId)) return [0, 1, 2, 3]
  if ([102, 104, 107, 109, 111, 113].includes(roleId)) return [0, 1, 2, 4]
  if ([103, 108, 112].includes(roleId)) return [0, 1, 2]
  return [0]
}

const behaviourIcon = (roleId, behaviour, side) => {
  if (behaviour === 0) return '•'
  if (behaviour === 1) return side === 'home' ? '↓' : '↑'
  if (behaviour === 2) return side === 'home' ? '↑' : '↓'
  const isRightSideRole = [101, 102, 106, 107, 111].includes(roleId)
  const pointsRight =
    behaviour === 3
      ? (side === 'home') === isRightSideRole
      : (side === 'home') !== isRightSideRole
  const towardsMiddle = pointsRight ? '→' : '←'
  const towardsWing = pointsRight ? '→' : '←'
  return behaviour === 3 ? towardsMiddle : towardsWing
}

const SpecTable = ({
  playerSpecs = {},
  playerStaminas = {},
  playerBehaviours = {},
  activePositions = {},
  onSpecChange = () => {},
  onStaminaChange = () => {},
  onBehaviourChange = () => {},
  onPositionActivate = () => {},
  onPositionDeactivate = () => {},
  activeColor = '#6ECDEA',
  side = 'home',
  tableStyle = {},
  className,
}) => {
  const activeFieldPlayerCount = Object.keys(activePositions).filter(
    roleId => Number(roleId) !== 100 && activePositions[roleId]
  ).length
  // Käännä layout jos vierasjoukkue
  let layout = specLayout
  if (side === 'away') {
    layout = [...specLayout].reverse().map(row => [...row].reverse())
  }

  return (
    <Table
      bordered
      variant="dark"
      size="sm"
      className={className}
      style={{ ...(tableStyle || {}), width: '100%' }}
    >
      <tbody>
        {layout.map((row, i) => (
          <tr key={i}>
            {row.map((roleId, j) => {
              const isActive = Boolean(activePositions[roleId])
              const isOverLimit =
                isActive &&
                Number(roleId) !== 100 &&
                activeFieldPlayerCount > 10 &&
                !defaultFieldRoleIds.includes(roleId)
              return (
              <td
                key={j}
                className={
                  roleId
                    ? 'simulator-spec-position-cell'
                    : 'simulator-spec-empty-cell'
                }
                onClick={() => roleId && !isActive && onPositionActivate(roleId)}
                style={{
                  minWidth: 60,
                  height: 60,
                  verticalAlign: 'middle',
                  background: roleId
                    ? isActive
                      ? isOverLimit
                        ? '#f59e0b33'
                        : `${activeColor}22`
                      : '#17191c'
                    : 'transparent',
                  border:
                    roleId && isActive
                      ? `2px solid ${isOverLimit ? '#f59e0b' : activeColor}`
                      : 'none',
                  position: 'relative',
                  cursor: roleId && !isActive ? 'pointer' : 'default',
                }}
              >
                {roleId && isActive ? (
                  <div>
                    <div
                      style={{ fontSize: 12, color: '#aaa', marginBottom: 2 }}
                    >
                      {roleTypes[roleId]}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: 2,
                      }}
                    >
                      <input
                        aria-label={`${roleTypes[roleId]} stamina`}
                        type="number"
                        min="1"
                        max="9"
                        value={playerStaminas[roleId] ?? 8}
                        onChange={event =>
                          onStaminaChange(
                            roleId,
                            Math.max(1, Math.min(9, Number(event.target.value) || 8))
                          )
                        }
                        style={{ width: 48, fontSize: 11 }}
                        title="Stamina"
                      />
                      {Number(roleId) !== 100 ? <Button
                        aria-label={`${roleTypes[roleId]} behaviour`}
                        type="button"
                        size="xs"
                        variant="outline-secondary"
                        onClick={event => {
                          event.stopPropagation()
                          const options = behaviourOptions(roleId)
                          const current = Number(playerBehaviours[roleId] ?? 0)
                          const currentIndex = options.indexOf(current)
                          onBehaviourChange(
                            roleId,
                            options[(currentIndex + 1) % options.length]
                          )
                        }}
                        style={{ width: 29, marginLeft: 3, padding: 0, fontSize: 14 }}
                        title="Vaihda käyttäytymisnuolta"
                      >
                        {behaviourIcon(
                          roleId,
                          Number(playerBehaviours[roleId] ?? 0),
                          side
                        )}
                      </Button> : (
                        <Button
                          type="button"
                          size="xs"
                          variant={
                            playerSpecs[roleId] === 4
                              ? 'primary'
                              : 'outline-secondary'
                          }
                          onClick={event => {
                            event.stopPropagation()
                            onSpecChange(
                              roleId,
                              playerSpecs[roleId] === 4 ? 0 : 4
                            )
                          }}
                          style={{
                            width: 29,
                            marginLeft: 3,
                            padding: 0,
                            background:
                              playerSpecs[roleId] === 4 ? activeColor : undefined,
                            color:
                              playerSpecs[roleId] === 4 ? '#23272A' : undefined,
                            borderColor:
                              playerSpecs[roleId] === 4 ? activeColor : undefined,
                          }}
                          title="Unpredictable"
                        >
                          <FontAwesomeIcon icon={specialtyIcons[4]} />
                        </Button>
                      )}
                      <select
                        aria-label={`${roleTypes[roleId]} behaviour`}
                        value={playerBehaviours[roleId] ?? 0}
                        onChange={event => onBehaviourChange(roleId, Number(event.target.value))}
                        style={{ display: 'none' }}
                        title="Behaviour"
                      >
                        <option value={0}>•</option>
                        <option value={1}>↑</option>
                        <option value={2}>↓</option>
                        <option value={3}>→</option>
                        {([111, 112, 113].includes(roleId)) ? <option value={4}>↗</option> : null}
                      </select>
                      {Number(roleId) !== 100 ? <Button
                        aria-label={`Poista ${roleTypes[roleId]} käytöstä`}
                        type="button"
                        size="xs"
                        variant="danger"
                        onClick={event => {
                          event.stopPropagation()
                          onPositionDeactivate(roleId)
                        }}
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          width: 16,
                          height: 16,
                          padding: 0,
                          fontSize: 13,
                          lineHeight: 1,
                        }}
                        title="Poista pelipaikka käytöstä"
                      >
                        ×
                      </Button> : null}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 4,
                      }}
                    >
                      {roleId !== 100 && allowedSpecialties(roleId).map(spec => (
                        <Button
                          key={spec}
                          variant={
                            playerSpecs[roleId] === spec
                              ? 'primary'
                              : 'outline-secondary'
                          }
                          type="button"
                          size="xs"
                          style={{
                            padding: '0 3px',
                            fontSize: '0.85rem',
                            lineHeight: '1',
                            minWidth: 20,
                            height: 24,
                            background:
                              playerSpecs[roleId] === spec
                                ? activeColor
                                : undefined,
                            color:
                              playerSpecs[roleId] === spec
                                ? '#23272A'
                                : undefined,
                            borderColor:
                              playerSpecs[roleId] === spec
                                ? activeColor
                                : undefined,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() =>
                            onSpecChange(
                              roleId,
                              playerSpecs[roleId] === spec ? 0 : spec
                            )
                          }
                          title={specialtyTypes[spec]}
                        >
                          <FontAwesomeIcon
                            icon={specialtyIcons[spec]}
                            style={{ fontSize: '0.95em' }}
                          />
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : roleId ? (
                  <div style={{ fontSize: 12, color: '#777' }}>
                    {roleTypes[roleId]}
                  </div>
                ) : null}
              </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

export default SpecTable
